import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { WidgetIcon } from '@procureai/ui-mui';
import { isNotEmpty } from 'ramda';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDomainType } from '~/common/types';
import { SourcingProjectSourceSystemField } from '~/common/types/cubes/SourcingProjectSourceSystem';
import { BaseDetailHeader, BaseDetailHeaderProps } from '~/components/BaseDetailHeader/BaseDetailHeader';
import { DomainIcon } from '~/components/DomainIcon/DomainIcon';

export interface SourcingProjectDetailHeaderProps {
  id: string;
  tabs?: BaseDetailHeaderProps['tabs'];
  actionsRight?: ReactNode;
}

export const SourcingProjectDetailHeader = ({
  id,
  tabs = undefined,
  actionsRight,
}: SourcingProjectDetailHeaderProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SourcingProjectDetail' });

  const filters: Filter[] = [
    {
      member: SourcingProjectSourceSystemField.INTERNAL_ID,
      operator: 'equals',
      values: [id],
    },
    {
      member: SourcingProjectSourceSystemField.IS_PRIMARY,
      operator: 'equals',
      values: ['true'],
    },
  ];
  const query = {
    dimensions: [
      SourcingProjectSourceSystemField.INTERNAL_ID,
      SourcingProjectSourceSystemField.SOURCE_REFERENCE,
      SourcingProjectSourceSystemField.LINK,
      SourcingProjectSourceSystemField.SOURCE_SYSTEM,
    ],
    filters,
  };

  const { isLoading, resultSet } = useCubeQuery({
    ...query,
  });

  const rawData = resultSet?.rawData() ?? [];
  const primarySourceReference = isNotEmpty(rawData)
    ? rawData[0][SourcingProjectSourceSystemField.SOURCE_REFERENCE]
    : undefined;

  const links = isNotEmpty(rawData)
    ? rawData.map((item) => ({
        title: t('sourceSystemLink.title', {
          sourceReference: item[SourcingProjectSourceSystemField.SOURCE_REFERENCE] ?? '',
          sourceSystem: item[SourcingProjectSourceSystemField.SOURCE_SYSTEM] ?? '',
        }),
        url: `${item[SourcingProjectSourceSystemField.LINK] ?? ''}`,
      }))
    : [];
  return (
    <BaseDetailHeader
      loading={isLoading}
      icon={<WidgetIcon icon={<DomainIcon domain={BaseDomainType.SOURCING_PROJECT} />} />}
      title={t('title', { id: primarySourceReference })}
      links={links}
      tabs={tabs}
      actionsRight={actionsRight}
    />
  );
};
